import { cloneElement, useCallback, useState } from 'react'
import {
  Accordion,
  Button,
  Group,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
  Tabs,
  Radio,
  Divider,
  Alert,
  LoadingOverlay,
  Modal,
} from '@mantine/core'
import {
  collectCredentialIssues,
  licenseRowSchema,
  npiNumberSchema,
  professionalRoleSchema,
  specialtySchema,
} from '@behuman/core-contract'

import { useOrganization } from '@/hooks/useOrganization'
import { useInviteOrgMember } from '@/hooks/useInviteOrgMember'
import { useCounter, useDisclosure } from '@mantine/hooks'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { IconSend, IconUserPlus } from '@tabler/icons-react'
import { LicensesField } from './LicensesField'
import { ProfessionalRoleFields } from './ProfessionalRoleFields'
import { BatchInvitePanel } from './batch-invite/BatchInvitePanel'

const inviteOrgMemberSchema = z
  .object({
    role: z.enum(['practitioner', 'scheduleManager']),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.email('Invalid email address').min(1, 'Email is required'),
    // Required only once a license row exists (the shared rules below, shown on the role).
    professionalRole: professionalRoleSchema.nullable(),
    specialty: specialtySchema.nullable(),
    npiNumber: npiNumberSchema.optional(),
    licenses: z.array(licenseRowSchema),
  })
  .superRefine((data, ctx) => {
    for (const issue of collectCredentialIssues(data)) {
      ctx.addIssue({ code: 'custom', ...issue })
    }
  })

type InviteOrgMemberValues = z.infer<typeof inviteOrgMemberSchema>

export function InviteOrgMemberModal({
  organizationId,
  trigger,
  onInvited,
}: {
  trigger: React.ReactElement<{ onClick: () => void }>
  organizationId?: string
  onInvited?: () => void
}) {
  const organization = useOrganization(organizationId)

  const [opened, { open, close }] = useDisclosure(false)
  const [step, stepActions] = useCounter(0, { min: 0, max: 3 })
  const [mode, setMode] = useState<'single' | 'batch'>('single')
  const [batchOnPreview, setBatchOnPreview] = useState(false)
  const [batchSending, setBatchSending] = useState(false)

  const form = useForm<InviteOrgMemberValues>({
    resolver: zodResolver(inviteOrgMemberSchema),
    defaultValues: {
      role: 'practitioner',
      firstName: '',
      lastName: '',
      email: '',
      professionalRole: null,
      specialty: null,
      npiNumber: '',
      licenses: [],
    },
  })

  const role = form.watch('role')

  const invite = useInviteOrgMember()

  const handleSubmit = useCallback(
    async (formData: InviteOrgMemberValues) => {
      if (!organizationId) return

      await invite.mutateAsync({
        organizationId,
        ...formData,
      })

      form.reset()
      stepActions.increment()
      onInvited?.()
    },
    [invite, organizationId, form, stepActions, onInvited],
  )

  return (
    <>
      {cloneElement(trigger, { onClick: open })}

      <Modal
        title={
          <Title size="h2" component="span">
            Invite new member to {organization.data?.name}
          </Title>
        }
        // The Members modal opens through the ModalsProvider, which renders one level above
        // Mantine's modal default: match it, and this later-mounted modal wins by DOM order.
        zIndex={201}
        size={mode === 'batch' && batchOnPreview ? '1100px' : 'lg'}
        opened={opened}
        withCloseButton={!batchSending}
        closeOnClickOutside={!batchSending}
        closeOnEscape={!batchSending}
        onClose={() => {
          if (batchSending) return
          setBatchOnPreview(false)
          close()
        }}
      >
        <LoadingOverlay
          zIndex={9999}
          overlayProps={{ backgroundOpacity: 1 }}
          visible={organization.isLoading}
        />

        <Stack>
          <Text c="dimmed">
            Invite a new member to join the organization with restricted access to
            organization-related resources.
          </Text>

          <SegmentedControl
            value={mode}
            onChange={(value) => setMode(value as 'single' | 'batch')}
            disabled={batchSending}
            data={[
              { value: 'single', label: 'Single member' },
              { value: 'batch', label: 'Batch (paste from Excel)' },
            ]}
          />

          <Divider />

          {/* Both modes stay mounted so each keeps its in-progress state while
              the modal is open; closing the modal unmounts (discards) both. */}
          <div style={mode === 'single' ? undefined : { display: 'none' }}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <Tabs value={step.toString()} onChange={(value) => stepActions.set(Number(value))}>
                <Tabs.List>
                  <Tabs.Tab value="0">Role Selection</Tabs.Tab>
                  <Tabs.Tab value="1">Member Details</Tabs.Tab>
                  <Tabs.Tab value="2">Permissions</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="0">
                  <Stack py="md">
                    <Title order={4}>Select Member Role</Title>
                    <Controller
                      name="role"
                      control={form.control}
                      render={({ field }) => (
                        <Radio.Group
                          ref={field.ref}
                          name={field.name}
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value)
                            // A Care Coordinator holds no credentials and the section hides:
                            // clear what was typed there, as the writer refuses it on that role.
                            if (value === 'scheduleManager') {
                              form.setValue('professionalRole', null)
                              form.setValue('specialty', null)
                              form.setValue('licenses', [])
                            }
                          }}
                          onBlur={field.onBlur}
                          label="Role Type"
                          description="Select the appropriate role for this member"
                          withAsterisk
                        >
                          <Stack mt="xs">
                            <Radio value="practitioner" label="Practitioner" />
                          </Stack>
                          <Stack mt="xs">
                            <Radio value="scheduleManager" label="Care Coordinator" />
                          </Stack>
                        </Radio.Group>
                      )}
                    />

                    <Alert color="blue" title="Role Information">
                      {role === 'practitioner' && (
                        <Text size="sm">
                          A <strong>Practitioner</strong> role is appropriate for doctors, nurses,
                          and other healthcare providers that will be providing care within this
                          organization.
                        </Text>
                      )}
                      {role === 'scheduleManager' && (
                        <Text size="sm">
                          A <strong>Care Coordinator</strong> role is responsible for maintaining
                          practitioners' schedules, managing appointment slots, and handling
                          appointment creation and modifications within the organization.
                        </Text>
                      )}
                    </Alert>

                    <Divider />
                    <Group justify="flex-end">
                      <Button onClick={stepActions.increment} type="button">
                        Next
                      </Button>
                    </Group>
                  </Stack>
                </Tabs.Panel>
                <Tabs.Panel value="1">
                  <Stack py="md">
                    <Title order={4}>Member Details</Title>

                    <Group grow align="flex-start">
                      <Controller
                        name="firstName"
                        control={form.control}
                        rules={{ required: 'First name is required' }}
                        render={({ field, fieldState }) => (
                          <TextInput
                            {...field}
                            label="First Name"
                            placeholder="Enter first name"
                            required
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                      <Controller
                        name="lastName"
                        control={form.control}
                        rules={{ required: 'Last name is required' }}
                        render={({ field, fieldState }) => (
                          <TextInput
                            {...field}
                            label="Last Name"
                            placeholder="Enter last name"
                            required
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                    </Group>
                    <Controller
                      name="email"
                      control={form.control}
                      rules={{ required: 'Email is required' }}
                      render={({ field, fieldState }) => (
                        <TextInput
                          {...field}
                          label="Email Address"
                          placeholder="Enter email address"
                          required
                          type="email"
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                    {role === 'practitioner' && (
                      <Accordion variant="contained">
                        <Accordion.Item value="credentials">
                          <Accordion.Control>Credentials (optional)</Accordion.Control>
                          <Accordion.Panel>
                            <Stack>
                              <ProfessionalRoleFields
                                control={form.control}
                                setValue={form.setValue}
                              />
                              <Controller
                                name="npiNumber"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                  <TextInput
                                    {...field}
                                    label="NPI Number"
                                    placeholder="Enter NPI number"
                                    error={fieldState.error?.message}
                                  />
                                )}
                              />
                              <LicensesField
                                control={form.control}
                                setValue={form.setValue}
                                variant="slim"
                              />
                            </Stack>
                          </Accordion.Panel>
                        </Accordion.Item>
                      </Accordion>
                    )}
                    <Divider />
                    <Group justify="flex-end">
                      <Button
                        onClick={async () => {
                          const isValid = await form.trigger()

                          if (isValid) {
                            stepActions.increment()
                          }
                        }}
                        type="button"
                      >
                        Next
                      </Button>
                    </Group>
                  </Stack>
                </Tabs.Panel>
                <Tabs.Panel value="2">
                  <Stack py="md">
                    <Title order={4}>Member Permissions</Title>
                    <Alert color="blue" title="Access Restrictions">
                      <Text size="sm">
                        This member will only have access to resources related to{' '}
                        {organization.data?.name}.
                      </Text>
                    </Alert>
                    <Divider />
                    <Group justify="flex-end">
                      <Button
                        leftSection={<IconSend size={16} />}
                        type="submit"
                        loading={invite.isPending}
                      >
                        Send Invitation
                      </Button>
                    </Group>
                  </Stack>
                </Tabs.Panel>
                <Tabs.Panel value="3">
                  <Stack py="md">
                    <Alert color="green" title="Invite sent successfully">
                      <Text size="sm">
                        An email invitation has been sent to the new member with instructions to
                        complete the registration process. You can invite another member by clicking
                        the button below.
                      </Text>
                    </Alert>
                    <Divider />
                    <Group justify="flex-end">
                      <Button
                        leftSection={<IconUserPlus size={16} />}
                        onClick={stepActions.reset}
                        type="button"
                      >
                        Invite Another Member
                      </Button>
                    </Group>
                  </Stack>
                </Tabs.Panel>
              </Tabs>
            </form>
          </div>

          <div style={mode === 'batch' ? undefined : { display: 'none' }}>
            <BatchInvitePanel
              organizationId={organizationId}
              onInvited={onInvited}
              onPreviewStepChange={setBatchOnPreview}
              onSendingChange={setBatchSending}
            />
          </div>
        </Stack>
      </Modal>
    </>
  )
}
